import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import type { PublicFormAuthOptions } from '@core/api/public/public-forms-api.service';

type CreateSessionResponse = {
  data: {
    sessionId: string;
    strategy: 'single' | 'multipart';
    uploadUrl?: string;
    parts?: Array<{ partNumber: number; uploadUrl: string }>;
    partSizeBytes?: number;
  };
};

type CompleteSessionResponse = {
  data: {
    fileId: string;
  };
};

@Injectable({ providedIn: 'root' })
export class SubmissionUploadApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.baseUrl.replace(/\/$/, '');

  async uploadSubmissionFile(input: {
    file: File;
    formId: string;
    fieldId: string;
    submissionId?: string | null;
    auth: PublicFormAuthOptions;
  }): Promise<string> {
    const kind = this.inferKind(input.file.type);
    const headers = this.headers(input.auth);

    const created = await firstValueFrom(
      this.http.post<CreateSessionResponse>(
        `${this.baseUrl}/api/storage/sessions`,
        {
          purpose: 'submission',
          kind,
          fileName: input.file.name || 'upload.bin',
          mimeType: input.file.type || 'application/octet-stream',
          sizeBytes: input.file.size,
          formId: input.formId,
          fieldId: input.fieldId,
          ...(input.submissionId ? { submissionId: input.submissionId } : {}),
        },
        { headers },
      ),
    );

    const session = created.data;
    if (session.strategy === 'single' && session.uploadUrl) {
      const put = await fetch(session.uploadUrl, {
        method: 'PUT',
        body: input.file,
        headers: {
          'Content-Type': input.file.type || 'application/octet-stream',
        },
      });
      if (!put.ok) {
        throw new Error('Failed to upload file.');
      }
    } else if (session.strategy === 'multipart' && session.parts?.length) {
      const etags: Array<{ partNumber: number; etag: string }> = [];
      const partSize = session.partSizeBytes ?? input.file.size;
      for (const part of session.parts) {
        const start = (part.partNumber - 1) * partSize;
        const end = Math.min(start + partSize, input.file.size);
        const blob = input.file.slice(start, end);
        const put = await fetch(part.uploadUrl, {
          method: 'PUT',
          body: blob,
        });
        if (!put.ok) {
          throw new Error('Failed to upload file part.');
        }
        const etag = put.headers.get('etag') ?? put.headers.get('ETag') ?? '';
        etags.push({
          partNumber: part.partNumber,
          etag: etag.replaceAll('"', ''),
        });
      }
      await firstValueFrom(
        this.http.post(
          `${this.baseUrl}/api/storage/sessions/${session.sessionId}/parts/ack`,
          { parts: etags },
          { headers },
        ),
      );
    } else {
      throw new Error('Upload session missing URLs.');
    }

    const completed = await firstValueFrom(
      this.http.post<CompleteSessionResponse>(
        `${this.baseUrl}/api/storage/sessions/${session.sessionId}/complete`,
        {},
        { headers },
      ),
    );

    return completed.data.fileId;
  }

  private inferKind(
    mimeType: string,
  ): 'image' | 'video' | 'audio' | 'pdf' | 'other' {
    if (mimeType.startsWith('image/')) {
      return 'image';
    }
    if (mimeType.startsWith('video/')) {
      return 'video';
    }
    if (mimeType.startsWith('audio/')) {
      return 'audio';
    }
    if (mimeType === 'application/pdf') {
      return 'pdf';
    }
    return 'other';
  }

  private headers(options: PublicFormAuthOptions): HttpHeaders {
    let headers = new HttpHeaders();
    if (options.respondentAccessToken) {
      headers = headers.set(
        'Authorization',
        `Bearer ${options.respondentAccessToken}`,
      );
    }
    if (options.formAccessToken) {
      headers = headers.set('X-Form-Access-Token', options.formAccessToken);
    }
    if (options.draftKey) {
      headers = headers.set('X-Form-Draft-Key', options.draftKey);
    }
    return headers;
  }
}
